import { useQuery } from '@tanstack/react-query';
import { EmployeeSchema, type EmployeeDto } from '@taqon/contracts';
import { useSearchParams } from 'react-router';
import { getData } from '../../shared/api/client';

/**
 * List filters kept in the URL, so a filtered list survives a refresh, can be bookmarked or shared,
 * and a dashboard figure can open the list behind it (FRD-CORE-043, FRD-RPT-007). An empty value
 * removes its key; changing a filter replaces the history entry rather than adding one per key.
 */
export function useUrlFilters<K extends string>(
  keys: readonly K[],
): [Readonly<Record<K, string>>, (key: K, value: string) => void] {
  const [params, setParams] = useSearchParams();
  const values = Object.fromEntries(keys.map((key) => [key, params.get(key) ?? ''])) as Record<
    K,
    string
  >;
  const set = (key: K, value: string) => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === '') next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };
  return [values, set];
}

/**
 * The employee an `employee` URL filter names, read so the picker can show who it is. Only for a
 * session that may read employees; the list filter applies by id either way.
 */
export function useFilterEmployee(id: string, enabled: boolean): EmployeeDto | null {
  const employee = useQuery({
    queryKey: ['hr', 'employee', id],
    enabled: enabled && id !== '',
    retry: false,
    queryFn: () => getData(`/api/v1/hr/employees/${encodeURIComponent(id)}`, EmployeeSchema),
  });
  return id === '' ? null : (employee.data ?? null);
}
